import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logError } from "helpers/log-helper/log-helper";
import { LinkService } from "services/Link.service";
import { setLocalStorage } from "helpers/local-storage-helper/local-storage-helper";
import { IRouteRequestState } from "types/routeRequestState";
import { Link } from "types/link";
import { LogPrefix } from "types/logging";
import { useConfirmationDialog } from "components/confirm-dialog/ConfirmDialog.context";
import { BSTLApiError } from "types/api-error";
import { setSessionStorage } from "helpers/session-storage-helper/session-storage-helper";
import { getCookieConsent } from "helpers/cookie-helper/cookie-helper";
import { createWebRedirectUrl } from "../helpers/Links.helper";

interface ILinkRouteRequestState extends IRouteRequestState {
	link?: Link;
	redirectWebUrlFromLink: { pathname: string; search: string };
}

/**
 * Handles all requests that need to be made initially for this route.
 * Initally and until all requests have finished, will return @param isLoading = true.
 * If any request fails, will return @param error with error contents.
 *
 * Used to display a shared initial loading and error state for a route.
 *
 * @returns shared loading state and error state for all requests.
 */
export const useLinksRouteRequests = (): ILinkRouteRequestState => {
	const { linkId } = useParams<"linkId">();
	const { t } = useTranslation();
	const { getConfirmationDialog } = useConfirmationDialog();
	const [state, setState] = useState<ILinkRouteRequestState>({
		isLoading: true,
		error: undefined,
		link: undefined,
		redirectWebUrlFromLink: {
			pathname: window.location.pathname,
			search: window.location.search,
		},
	});

	useEffect(() => {
		const fetch = async () => {
			try {
				if (!linkId) throw new Error();
				const link = await LinkService.getLink(linkId);
				const redirectWebUrlFromLink = createWebRedirectUrl(link);
				// Store resolved link data locally
				setLocalStorage("resolvedAppLink", link);

				if (getCookieConsent()?.analytics) {
					// Per session we track usage of the resolved link
					setSessionStorage("initiatedFromAppLink", true);
					setSessionStorage("initiatedFromAppLinkId", linkId);
				}

				setState((prev) => ({
					...prev,
					isLoading: false,
					link,
					redirectWebUrlFromLink,
				}));
			} catch (error) {
				setState((prev) => ({ ...prev, isLoading: false, error }));
				logError(LogPrefix.Link, error, "Could not get app link");

				if (error instanceof BSTLApiError && error.statusCode === 404) {
					getConfirmationDialog({
						title: t("qr_code_invalid"),
						message: t("qr_scanned_code_invalid"),
						confirmButtonText: t("action_ok"),
						showCloseButton: false,
					});
				} else {
					getConfirmationDialog({
						title: t("qr_code_unreadable"),
						message: t("qr_scanned_code_unreadable"),
						confirmButtonText: t("action_ok"),
						showCloseButton: false,
					});
				}
			}
		};
		fetch();
	}, [getConfirmationDialog, linkId, t]);

	return state;
};
